// store/slices/authSlice.ts
import { authApi, type User } from "@/services/authApi";
import { storage } from "@/utils/storage";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface AuthState {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  token: null,
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

// Async thunk for login
export const login = createAsyncThunk(
  "auth/login",
  async (
    { email, password }: { email: string; password: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await authApi.login(email, password);
      // Store token and full user object (including organization) in AsyncStorage
      await storage.setToken(response.token);
      await storage.setUser(response.user);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk to restore session from storage (called on app start)
export const restoreSession = createAsyncThunk(
  "auth/restoreSession",
  async () => {
    const [token, user] = await Promise.all([
      storage.getToken(),
      storage.getUser(),
    ]);
    if (token && user) {
      return { token, user };
    }
    return null;
  },
);

// Async thunk for logout
export const logout = createAsyncThunk("auth/logout", async () => {
  await storage.clearAuth();
  return null;
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(login.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.user = action.payload.user;
    });
    builder.addCase(login.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
    });
    // Restore session
    builder.addCase(restoreSession.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(restoreSession.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
      } else {
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
      }
    });
    builder.addCase(restoreSession.rejected, (state) => {
      state.loading = false;
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
    });
    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.error = null;
    });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
